import React from 'react'

import {
  AttentionOutlined,
  InfoOutlined,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, shape, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import { formatLongDate } from 'Utils/FormatDate'

import { SUBSCRIPTION_STATUS, resolveDisplayStatus } from '../constants'
import StatusBanner from './StatusBanner'
import { RETRY_STATE } from '../hooks/useSubscription'

const BANNER_CONFIG = {
  [SUBSCRIPTION_STATUS.PAST_DUE]: {
    icon: AttentionOutlined,
    titleKey: 'subscription_management.status_banners.past_due.title',
    descriptionKey:
      'subscription_management.status_banners.past_due.description',
    variant: 'attention',
    getDescriptionParams: (sub) => ({ date: sub.renewsOn }),
  },
  [SUBSCRIPTION_STATUS.REDEMPTION]: {
    icon: InfoOutlined,
    titleKey: 'subscription_management.status_banners.redemption.title',
    descriptionKey:
      'subscription_management.status_banners.redemption.description',
    variant: 'redemption',
  },
  [SUBSCRIPTION_STATUS.PENDING_CANCEL]: {
    icon: InfoOutlined,
    descriptionKey:
      'subscription_management.status_banners.cancelled.description',
    variant: 'attention',
    getDescriptionParams: (sub) => ({ date: sub.periodEndDate }),
    isHidden: (sub) => sub.canceledByAdmin,
  },
  [SUBSCRIPTION_STATUS.PAUSE_SCHEDULED]: {
    icon: InfoOutlined,
    titleKey: 'subscription_management.status_banners.pause_scheduled.title',
    descriptionKey:
      'subscription_management.status_banners.pause_scheduled.description',
    variant: 'info',
    getTitleParams: (sub, locale) => ({ date: formatLongDate(sub.userPauseScheduledAt, locale) }),
  },
  [SUBSCRIPTION_STATUS.PAUSED]: {
    icon: InfoOutlined,
    titleKey: 'subscription_management.status_banners.paused.title',
    // Operational (chargeback/admin) pauses are not user-resumable, so their copy
    // must not invite the user to resume from this page.
    getDescriptionKey: (sub) => (sub.userPause
      ? 'subscription_management.status_banners.paused.description'
      : 'subscription_management.status_banners.paused.description_operational'),
    variant: 'info',
  },
  [SUBSCRIPTION_STATUS.FULLY_CANCELED]: {
    icon: InfoOutlined,
    descriptionKey:
      'subscription_management.status_banners.ended.description',
    variant: 'info',
    getDescriptionParams: (sub) => ({ date: sub.periodEndDate }),
    isHidden: (sub) => sub.canceledByAdmin,
  },
  [SUBSCRIPTION_STATUS.EXPIRED]: {
    icon: InfoOutlined,
    descriptionKey:
      'subscription_management.status_banners.ended.description',
    variant: 'info',
    getDescriptionParams: (sub) => ({ date: sub.periodEndDate }),
  },
  [SUBSCRIPTION_STATUS.SUSPENDED]: {
    icon: InfoOutlined,
    titleKey: 'subscription_management.status_banners.suspended.title',
    variant: 'danger',
    // Use renderCustomDescription only when description requires custom JSX (e.g. inline links).
    // Prefer descriptionKey + getDescriptionParams for standard cases.
    renderCustomDescription: (sub, translate, locale) => {
      const formattedDate = formatLongDate(sub.suspendedAt, locale)
      const descriptionKey = formattedDate
        ? 'subscription_management.status_banners.suspended.description'
        : 'subscription_management.status_banners.suspended.description_no_date'
      const descriptionParams = formattedDate ? { date: formattedDate } : {}

      return (
        <>
          {translate(descriptionKey, descriptionParams)}
          {' '}
          <a href="/contact-us" className="cgt-link-secondary-accented">
            {translate('subscription_management.status_banners.suspended.contact_link')}
          </a>
          {' '}
          {translate('subscription_management.status_banners.suspended.description_suffix')}
        </>
      )
    },
  },
}

function resolveBannerKey({ status, canceledAt, userPauseScheduledAt }) {
  const displayStatus = resolveDisplayStatus(status, canceledAt, userPauseScheduledAt)
  return BANNER_CONFIG[displayStatus] ? displayStatus : null
}

function SubscriptionStatusBanner({
  subscription,
  locale = 'en',
  retryState = RETRY_STATE.IDLE,
  resumeRequested = false,
}) {
  const translate = (key, options) => I18n.t(key, { ns: 'rails', lng: locale, ...options })

  const displayStatus = resolveDisplayStatus(
    subscription.status,
    subscription.canceledAt,
    subscription.userPauseScheduledAt
  )
  const isRedemption = displayStatus === SUBSCRIPTION_STATUS.REDEMPTION

  if (retryState === RETRY_STATE.SUBMITTED && isRedemption) {
    return (
      <StatusBanner
        icon={InfoOutlined}
        title={translate('subscription_management.retry_payment.processing.title')}
        description={translate('subscription_management.retry_payment.processing.description')}
        variant="progress"
      />
    )
  }

  // A resume/cancel-pause request was accepted; Solidgate webhooks drive the actual state
  // change, so show progress instead of the stale paused banner until the page reloads.
  if (resumeRequested) {
    return (
      <StatusBanner
        icon={InfoOutlined}
        title={translate('subscription_management.status_banners.resume_requested.title')}
        description={translate('subscription_management.status_banners.resume_requested.description')}
        variant="progress"
      />
    )
  }

  const bannerKey = resolveBannerKey(subscription)
  const config = BANNER_CONFIG[bannerKey]

  if (!config) return null
  if (config.isHidden?.(subscription)) return null

  const description = config.renderCustomDescription
    ? config.renderCustomDescription(subscription, translate, locale)
    : translate(
      config.getDescriptionKey?.(subscription) || config.descriptionKey,
      config.getDescriptionParams?.(subscription) || {}
    )

  return (
    <StatusBanner
      icon={config.icon}
      title={config.titleKey
        ? translate(config.titleKey, config.getTitleParams?.(subscription, locale) || {})
        : null}
      description={description}
      variant={config.variant}
    />
  )
}

SubscriptionStatusBanner.propTypes = {
  subscription: shape({
    status: string.isRequired,
    canceledAt: string,
    canceledByAdmin: bool,
    suspendedAt: string,
    renewsOn: string,
    periodEndDate: string,
    userPauseScheduledAt: string,
    userPause: bool,
  }).isRequired,
  locale: string,
  retryState: string,
  resumeRequested: bool,
}

export default SubscriptionStatusBanner
